import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http'
import { API_BASE_URL, API_RETRY_COUNT, API_RETRY_DELAY_MS } from './http-tokens'
import { catchError, Observable, throwError } from 'rxjs'
import { normalizeHttpError } from './http-error.handler'
import { retryWithBackoff } from './http-operators'

// Narrow wrapper around HttpClient to enforce consistent base URL,
// retry policy, and normalized error mapping. Keep it minimal and generic
// so it remains easy to swap/extend later.
export interface ApiRequestOptions {
  params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> }
  headers?: HttpHeaders | { [header: string]: string | string[] }
  context?: HttpContext
  /**
   * When true, send cookies/authorization headers in cross-site requests.
   * Required for session-cookie based authentication flows (e.g. SSO callback).
   */
  withCredentials?: boolean
}

@Injectable({ providedIn: 'root' })
export class ApiClient {
  private readonly http = inject(HttpClient)
  private readonly baseUrl = inject(API_BASE_URL, { optional: true }) ?? ''
  private readonly retryCount = inject(API_RETRY_COUNT, { optional: true }) ?? 0
  private readonly retryDelay = inject(API_RETRY_DELAY_MS, { optional: true }) ?? 250

  get<T>(path: string, options: ApiRequestOptions = {}): Observable<T> {
    return this.http.get<T>(this.join(path), options).pipe(
      retryWithBackoff(this.retryCount, this.retryDelay),
      catchError(e => throwError(() => normalizeHttpError(e)))
    )
  }

  post<T>(path: string, body: unknown, options: ApiRequestOptions = {}): Observable<T> {
    return this.http.post<T>(this.join(path), body, options).pipe(
      retryWithBackoff(this.retryCount, this.retryDelay),
      catchError(e => throwError(() => normalizeHttpError(e)))
    )
  }

  put<T>(path: string, body: unknown, options: ApiRequestOptions = {}): Observable<T> {
    return this.http.put<T>(this.join(path), body, options).pipe(
      retryWithBackoff(this.retryCount, this.retryDelay),
      catchError(e => throwError(() => normalizeHttpError(e)))
    )
  }

  patch<T>(path: string, body: unknown, options: ApiRequestOptions = {}): Observable<T> {
    return this.http.patch<T>(this.join(path), body, options).pipe(
      retryWithBackoff(this.retryCount, this.retryDelay),
      catchError(e => throwError(() => normalizeHttpError(e)))
    )
  }

  delete<T>(path: string, options: ApiRequestOptions = {}): Observable<T> {
    return this.http.delete<T>(this.join(path), options).pipe(
      retryWithBackoff(this.retryCount, this.retryDelay),
      catchError(e => throwError(() => normalizeHttpError(e)))
    )
  }

  getBlob(path: string): Observable<Blob> {
    return this.http
      .get(this.join(path), { responseType: 'blob' })
      .pipe(catchError(e => throwError(() => normalizeHttpError(e))))
  }

  private join(path: string): string {
    // Join baseUrl and path, avoiding duplicated slashes.
    if (!this.baseUrl) return path
    const slash = this.baseUrl.endsWith('/') || path.startsWith('/') ? '' : '/'
    return `${this.baseUrl}${slash}${path}`
  }
}
